import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import classService from '../services/classService';
import './InstructorProfile.css';

const InstructorProfile = () => {
  const { instructorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Fallback data passed via navigation state
  const navState = location.state || {};
  const fallbackName = navState.instructorName || '';
  const fallbackId = navState.instructorId || instructorId;

  const [instructor, setInstructor] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const idToFetch = fallbackId && parseInt(fallbackId) > 0 ? fallbackId : instructorId;
      try {
        if (idToFetch && parseInt(idToFetch) > 0) {
          const [details, mediaRes] = await Promise.all([
            classService.getInstructorById(idToFetch).catch(() => null),
            classService.getInstructorMedia(idToFetch).catch(() => []),
          ]);
          const raw = Array.isArray(details) ? details[0] : details;
          // Only set if we got real data with at least a name
          if (raw && (raw.Instructor_First_Name || raw.instructor_First_Name)) {
            setInstructor(raw);
            // Photo may be included directly in the instructor response
            const directPhoto = raw.Instructor_Media_URL || raw.instructor_Media_URL || '';
            if (directPhoto) setPhotoUrl(directPhoto);
          }
          // Also check dedicated media endpoint
          if (!mediaRes || (Array.isArray(mediaRes) && mediaRes.length === 0)) return;
          const media = Array.isArray(mediaRes) ? mediaRes : [];
          const mainPhoto = media.find(m => m.Is_Main === 'Y' || m.is_Main === 'Y') || media[0];
          if (mainPhoto) {
            setPhotoUrl(mainPhoto.Instructor_Media_URL || mainPhoto.instructor_Media_URL || '');
          }
        }
      } catch {
        // silent — we'll show fallback UI
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [instructorId]);

  const firstName = instructor?.Instructor_First_Name || instructor?.instructor_First_Name || '';
  const lastName = instructor?.Instructor_Last_Name || instructor?.instructor_Last_Name || '';
  const fullName = `${firstName} ${lastName}`.trim() || fallbackName;
  const description = instructor?.Instructor_Description || instructor?.instructor_Description || '';
  const qualification = instructor?.Instructor_Qualification || instructor?.instructor_Qualification || '';
  const email = instructor?.Instructor_Email || instructor?.instructor_Email || '';
  const phone = instructor?.Instructor_Phone || instructor?.instructor_Phone || '';
  const initials = fullName.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  // Show card if we have at least a name (from API or nav state)
  const hasProfile = !!fullName;

  return (
    <div className="instructor-profile-page min-h-screen">
      {/* Back button */}
      <div className="max-w-3xl mx-auto px-4 pt-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Classes
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-32">
          <div className="ip-loading-pill flex items-center gap-3 px-6 py-3 rounded-full shadow-sm" style={{ background: '#fff7ed', border: '1px solid #fed7aa' }}>
            <div className="animate-spin rounded-full h-5 w-5 border-2" style={{ borderColor: '#fed7aa', borderTopColor: '#f97316' }}></div>
            <span className="text-sm font-medium" style={{ color: '#c2410c' }}>Loading profile…</span>
          </div>
        </div>
      )}

      {/* Not found */}
      {!loading && !hasProfile && (
        <div className="max-w-md mx-auto mt-16 px-4 text-center">
          <div className="ip-not-found-card bg-white rounded-3xl p-12 shadow-sm" style={{ border: '1px solid #fde68a' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#fef3c7' }}>
              <svg className="w-8 h-8" style={{ color: '#d97706' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <p className="font-semibold text-lg" style={{ color: '#78716c' }}>Instructor profile not available</p>
            <button onClick={() => navigate(-1)} className="mt-4 text-sm font-medium underline" style={{ color: '#f97316' }}>Go Back</button>
          </div>
        </div>
      )}

      {/* Profile card */}
      {!loading && hasProfile && (
        <div className="max-w-3xl mx-auto px-4 pb-16 pt-6">
          <div className="ip-card" style={{ background: '#ffffff', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', border: '1px solid #fed7aa' }}>

            {/* Hero header — photo lives INSIDE so nothing gets clipped */}
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', background: 'linear-gradient(135deg,#f97316 0%,#fb923c 40%,#fbbf24 100%)', minHeight: '280px', paddingBottom: '24px' }}>
              {/* Decorative circles */}
              <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.10)' }}></div>
              <div style={{ position: 'absolute', top: '30px', left: '-30px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }}></div>
              <div style={{ position: 'absolute', bottom: '20px', left: '25%', width: '70px', height: '70px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }}></div>
              {/* YK badge */}
              <div style={{ position: 'absolute', top: '16px', right: '20px', background: 'rgba(255,255,255,0.22)', color: '#ffffff', padding: '4px 12px', borderRadius: '999px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                Yugal Kunj Instructor
              </div>
              {/* Photo — fully inside header, no clipping */}
              <div style={{ position: 'relative', zIndex: 10 }}>
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={fullName}
                    onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                    style={{ width: '200px', height: '200px', borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: '6px solid rgba(255,255,255,0.95)', boxShadow: '0 16px 48px rgba(0,0,0,0.25)', display: 'block' }}
                  />
                ) : (
                  <div className="flex items-center justify-center" style={{ width: '200px', height: '200px', borderRadius: '50%', border: '6px solid rgba(255,255,255,0.95)', background: 'rgba(255,255,255,0.25)', boxShadow: '0 16px 48px rgba(0,0,0,0.18)' }}>
                    <span className="text-6xl font-extrabold" style={{ color: '#ffffff' }}>{initials || '?'}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Name + contact — in white section */}
            <div className="ip-card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 24px 8px', background: '#ffffff' }}>
              <h1 className="ip-name text-3xl font-extrabold tracking-tight text-center" style={{ color: '#1c1917' }}>{fullName}</h1>
              {qualification && (
                <span className="inline-block mt-2 px-4 py-1 rounded-full text-sm font-semibold" style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>
                  🎓 {qualification}
                </span>
              )}
              {/* Contact chips */}
              <div className="flex flex-wrap items-center justify-center gap-2 mt-4 mb-4">
                {phone && (
                  <a
                    href={`tel:${phone}`}
                    className="ip-chip inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:shadow-md"
                    style={{ background: '#fff7ed', color: '#92400e', border: '1px solid #fde68a', textDecoration: 'none' }}
                  >
                    <svg className="ip-chip-icon w-4 h-4" style={{ color: '#f97316' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {phone}
                  </a>
                )}
                {email && (
                  <a
                    href={`mailto:${email}`}
                    className="ip-chip inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:shadow-md"
                    style={{ background: '#fff7ed', color: '#92400e', border: '1px solid #fde68a', textDecoration: 'none' }}
                  >
                    <svg className="ip-chip-icon w-4 h-4" style={{ color: '#f97316' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {email}
                  </a>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="ip-divider" style={{ height: '1px', background: 'linear-gradient(90deg,transparent,#fed7aa,transparent)', margin: '0 40px' }}></div>

            {/* Bio section */}
            <div className="ip-card-body" style={{ padding: '24px 40px 32px', background: '#ffffff' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-6 rounded-full" style={{ background: '#f97316' }}></div>
                <h2 className="ip-about-label" style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#78716c', margin: 0 }}>About</h2>
              </div>
              {description ? (
                <div
                  className="ip-bio-text"
                  style={{ color: '#44403c', lineHeight: '1.8', fontSize: '15px' }}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              ) : (
                <p className="text-sm italic" style={{ color: '#a8a29e' }}>No bio available for this instructor.</p>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default InstructorProfile;
